//// Application Configuration
import local_configuration from '../configurations/iat-ui-config.json'

//// Application Packages
import React, { useEffect } from 'react'
import {
    useSearchParams,
    useLocation,
    useNavigate,
    ScrollRestoration,
} from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import axios from 'axios'
import {
    format,
    parseISO,
    differenceInSeconds,
    differenceInMinutes,
} from 'date-fns'
import {
    extractSettingsDataSession,
    extractAuthorizationsDataSession,
    extractAccountDataSession,
    extractFormsDataSession,
    extractCacheDataSession,
} from '../functions/session-functions'

//// Application Slices
import {
    updateSettings,
    deleteSettings,
    selectSettings,
} from '../reducers/settingSlice'
import { createCache } from '../reducers/cacheSlice'
import {
    createAuthorizations,
    deleteAuthorizations,
    selectAuthorizations,
} from '../reducers/authorizationSlice'
import {
    createAccountAuxiliary,
    deleteAccountData,
    deleteAccountValidity,
    deleteAccountAuxiliary,
    selectAccount,
} from '../reducers/accountSlice'
import {
    createFormAuxiliary,
    createSubFormValidity,
    updateFormAuxiliary,
    deleteFormData,
    deleteFormValidity,
    deleteFormAuxiliary,
    updateSubFormAuxiliary,
    deleteSubFormData,
    deleteSubFormValidity,
    deleteSubFormAuxiliary,
} from '../reducers/formSlice'

//// Application Components
import PageNavigationComponent from '../components/navigation-components/page-menu-navigation-component'
import FormMenuNavigationComponent from '../components/navigation-components/form-menu-navigation-component'
import BreadcrumbComponent from '../components/navigation-components/breadcrumb-component'
import FooterComponent from '../components/navigation-components/footer-component'
import LoadingComponent from '../components/simple-components/loading-component'
import ActivityTimerComponent from '../components/system-components/activity-timer-component'

//// Carbon Components

//// Carbon Icons

//// Static Constants

//////// Component
const Page = ({ children }) => {
    //////// Hooks
    const dispatch = useDispatch()
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { pathname } = location
    const fullScreen = useMediaQuery({ query: '(min-width: 1400px)' })
    const notFullScreen = useMediaQuery({ query: '(max-width: 1400px)' })

    //////// State

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const auth = useSelector(selectAuthorizations)
    const authorizationsData = useSelector(selectAuthorizations)
    const accountData = useSelector(selectAccount)

    //// Slice Data
    const configuration = settingsData?.['configuration']
    const initialized = settingsData?.['interface']?.['initialized']
    const form = auth?.['tokens']?.['form']

    //////// Search Params
    const categorySearchParam = searchParams.get('category')
    const pageSearchParam = searchParams.get('page')
    const formSearchParam = searchParams.get('form')
    const clientSearchParam = searchParams.get('client')

    //////// Methods

    // Orchestrate Read Configuration From Database (Integration)
    const orchestrateReadConfiguration = async () => {
        try {
            const base_url = `${process.env.REACT_APP_BASE_URL}`
            const api_endpoint = `${process.env.REACT_APP_ORCHESTRATION_ENDPOINT_PROXY_SYSTEM_CONFIGURATION}`
            const url = base_url + api_endpoint
            const { data } = await axios.post(url, {})
            if (data) {
                return await data
            } else {
                return null
            }
        } catch (error) {
            console.warn('Load Configuration From Database Error:', error)
            return null
        }
    }

    // Orchestrate Create Session Log (Integration)
    const orchestrateCreationSessionLog = async (
        cookie,
        reguserid,
        session
    ) => {
        try {
            const base_url = `${process.env.REACT_APP_BASE_URL}`
            const api_endpoint = `${process.env.REACT_APP_ORCHESTRATION_ENDPOINT_PROXY_SYSTEM_SESSION_CREATION}`
            const url = base_url + api_endpoint
            const { data } = await axios.post(url, {
                session_id: cookie,
                reguserid: reguserid,
                session: session,
            })
            if (data) {
                return await data
            } else {
                return null
            }
        } catch (error) {
            console.warn('Create Session Log Error:', error)
            return null
        }
    }

    // Read Configuration From Database And Write To Redux (If It Does Not Exist)
    const createConfiguration = async () => {
        if (settingsData?.['configuration']) {
            const confArr = Object?.keys(settingsData?.['configuration'])
            const test = confArr?.length > 0 ? true : false
            const then = settingsData?.interface?.configured_timestamp
            const now = format(new Date(), "yyyy-MM-dd'T'HH:mm:ssXX").toString()
            const diff = differenceInMinutes(parseISO(now), parseISO(then))
            const frequency = parseInt(
                process.env
                    .REACT_APP_CONFIGURATION_PERSISTANCE_FREQUENCY_MINUTES
            )

            if (!test || diff > frequency) {
                // Call Read Configuration Database API
                const configuration = await orchestrateReadConfiguration()

                // Check For Configuration
                if (
                    configuration?.['config_exists'] &&
                    configuration?.['config']
                ) {
                    // Dispatch Configuration Data
                    dispatch(
                        updateSettings({
                            digital_name: 'configuration',
                            settings: configuration?.config,
                        })
                    )

                    return await settingsData?.configuration
                } else {
                    // Load Local Configuration
                    const backup_configuration = local_configuration

                    // Check For Local Configuration
                    if (backup_configuration) {
                        // Dispatch Configuration Data
                        dispatch(
                            updateSettings({
                                digital_name: 'configuration',
                                settings: backup_configuration,
                            })
                        )

                        return await settingsData?.configuration
                    } else {
                        return false
                    }
                }
            } else {
                return await settingsData?.configuration
            }
        } else {
            return false
        }
    }

    // Load Application Configuration From Redux (If It Does Not Exist)
    const createApplicationLevelConfig = async () => {
        if (settingsData?.['application']) {
            const appArr = Object?.keys(settingsData?.['application'])
            const test = appArr?.length > 0 ? true : false
            if (!test) {
                if (settingsData?.['configuration']?.['application']) {
                    // Dispatch Application Data
                    dispatch(
                        updateSettings({
                            digital_name: 'application',
                            settings: settingsData?.configuration?.application,
                        })
                    )

                    return true
                } else {
                    return false
                }
            } else {
                return await settingsData?.application
            }
        } else {
            return false
        }
    }

    // Load Interface Configuration From Redux (If It Does Not Exist)
    const createInterfaceLevelConfig = async () => {
        if (settingsData?.['interface']) {
            const intrArr = Object?.keys(settingsData?.['interface'])
            const test = intrArr?.length > 0 ? true : false
            if (!test) {
                if (settingsData?.configuration?.['interface']) {
                    // Dispatch Interface Data
                    dispatch(
                        updateSettings({
                            digital_name: 'interface',
                            settings: settingsData?.configuration?.interface,
                        })
                    )

                    return true
                } else {
                    return false
                }
            } else {
                return await settingsData?.interface
            }
        } else {
            return false
        }
    }

    // Load Page Level Configuration From Redux Based On Pathname
    const createPageLevelConfig = async (pathname) => {
        const config = {}

        // Extract Page Data
        if (pathname) {
            const temp = {}
            const path = pathname.split('/')

            // Extract Category & Page/Form Name By Path
            if (path?.length > 0 && path?.length < 3) {
                // Example: / + Path
                temp.digital_name = await path?.[1]
            } else if (path?.length > 2) {
                // Example: / + Category + / + Path
                temp.category_digital_name = await path?.[1]
                temp.digital_name = await path?.[2]
            }

            // Check For Category & Form
            if (temp?.category_digital_name && temp?.digital_name) {
                // Set Temporary Category Object
                temp.category = await settingsData?.configuration?.forms?.[
                    temp?.category_digital_name
                ]

                // Set Temporary Form Object
                temp.form = await settingsData?.configuration?.forms?.[
                    temp?.category_digital_name
                ]?.[temp?.digital_name]
            } else if (temp?.digital_name) {
                // Set Temporary Category Object
                temp.category = await settingsData?.configuration?.forms?.[
                    temp?.digital_name
                ]

                // Set Temporary Form Object
                temp.form = await settingsData?.configuration?.forms?.[
                    temp?.digital_name
                ]?.['introduction']
            }

            // Check For Page
            if (!temp?.category && !temp?.form && temp?.digital_name) {
                // Set Temporary Page Object
                temp.page = await settingsData?.configuration?.pages?.[
                    temp?.digital_name
                ]
            }

            // Check Form Or Page Object Else Check For Page
            if (temp?.category && temp?.form) {
                // Set Category & Form Object
                config.category = await temp?.category
                config.form = await temp?.form
            } else if (temp?.page) {
                // Set Page Object
                config.page = await temp?.page
            }
        }

        // Dispatch To Clear Data
        dispatch(
            deleteSettings({
                digital_name: 'interface',
            })
        )
        dispatch(
            deleteSettings({
                digital_name: 'category',
            })
        )
        dispatch(
            deleteSettings({
                digital_name: 'page',
            })
        )
        dispatch(
            deleteSettings({
                digital_name: 'form',
            })
        )
        dispatch(
            updateSettings({
                digital_name: 'interface',
                settings: {
                    initialized: false,
                    full_screen_width: fullScreen ? true : false,
                    not_full_screen_width: notFullScreen ? true : false,
                    view_expanded: fullScreen ? true : false,
                    side_nav_expanded: fullScreen ? true : false,
                    view_title: '',
                    view_type: '',
                    view_links: [],
                },
            })
        )
        dispatch(
            updateSettings({
                digital_name: 'category',
                settings: {},
            })
        )
        dispatch(
            updateSettings({
                digital_name: 'page',
                settings: {},
            })
        )
        dispatch(
            updateSettings({
                digital_name: 'form',
                settings: {},
            })
        )

        // Dispatch View Data
        if (config?.page) {
            // console.info('Successfully Loaded Page Configuration')

            // Dispatch Page Data
            dispatch(
                updateSettings({
                    digital_name: 'page',
                    settings: config?.page,
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'interface',
                    settings: {
                        view_type: 'page',
                        view_title: config?.page?.written_name,
                    },
                })
            )

            return true
        } else if (config?.category && config?.form) {
            // console.info('Successfully Loaded Category & Form Configuration')

            // Dispatch Category & Form Data
            dispatch(
                updateSettings({
                    digital_name: 'category',
                    settings: config?.category,
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'form',
                    settings: config?.form,
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'interface',
                    settings: {
                        view_type: 'form',
                        view_title: config?.form?.written_name,
                    },
                })
            )

            return true
        } else if (!config?.page && !config?.category && !config?.form) {
            // Dispatch Fallback Page Data

            // console.warn(
            //     'Failed To Load Category & Form Or Page Configuration From Redux'
            // )

            dispatch(
                updateSettings({
                    digital_name: '',
                    settings: {},
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'interface',
                    settings: {
                        view_type: '',
                        view_title: '',
                    },
                })
            )

            return true
        } else {
            // console.warn('Failed To Dispatch Configuration From Redux')

            return false
        }
    }

    // Read Session From Database And Write To Redux (If It Does Not Exist)
    const createSession = async () => {
        const settings = extractSettingsDataSession()
        const authorizations = extractAuthorizationsDataSession()
        const account = extractAccountDataSession()
        const forms = extractFormsDataSession()

        // Check For Redux Data
        if (settings && authorizations && account && forms) {
            const session = {
                settings: settings,
                authorizations: authorizations,
                account: account,
                forms: forms,
            }

            return session
        } else {
            return false
        }
    }

    // Create Session Log In Database
    const createSessionLog = async (cookie, reguserid) => {
        const settings = extractSettingsDataSession()
        const cache = extractCacheDataSession()

        // Check For User
        if (cookie && reguserid) {
            // Check For Active Session
            const active = settings?.session?.active
            if (active) {
                // Check For Session Data
                const session = cache?.['session']
                if (session) {
                    const now = format(
                        new Date(),
                        "yyyy-MM-dd'T'HH:mm:ssXX"
                    ).toString()
                    const saved = await settingsData?.session?.saved
                    const diff = differenceInSeconds(
                        parseISO(now),
                        parseISO(saved)
                    )
                    const frequency = parseInt(
                        process.env.REACT_APP_SESSION_SAVE_FREQUENCY_SECONDS
                    )

                    // Check That Difference Is 15 Seconds
                    if (diff > frequency) {
                        // Call Creation Session Log API
                        const log = await orchestrateCreationSessionLog(
                            cookie,
                            reguserid,
                            session
                        )

                        if (log?.['session_created']) {
                            return true
                        } else {
                            return false
                        }
                    } else {
                        return true
                    }
                } else {
                    // console.warn('Failed Logging Session (No Session Data)')

                    return false
                }
            } else {
                // console.info('No Session To Log')

                return true
            }
        } else {
            return true
        }
    }

    //////// Functional Methods

    // Handle Logout
    const handleLogout = () => {
        //// Perform Browser Effects

        localStorage.clear()
        sessionStorage.clear()

        //// End Perform Browser Effects

        // Clear Authorizations
        dispatch(deleteAuthorizations())

        //// Paginated Data
        if (local_configuration) {
            const config = Object?.keys(local_configuration)
            if (config?.length > 0) {
                //// Clear Account Data, Errors & Settings
                const account_config = local_configuration?.['pages']
                if (account_config) {
                    const titles = Object?.keys(account_config)
                    if (titles?.length > 0) {
                        titles?.forEach((el) => {
                            dispatch(
                                deleteAccountData({
                                    digital_name: el,
                                })
                            )
                            dispatch(
                                deleteAccountValidity({
                                    digital_name: el,
                                })
                            )
                            dispatch(
                                deleteAccountAuxiliary({
                                    digital_name: el,
                                })
                            )
                        })
                    }
                }

                //// Clear Form Data, Errors & Settings
                const forms_config = local_configuration?.['forms']
                if (forms_config) {
                    const categories = Object?.keys(forms_config)
                    if (categories?.length > 0) {
                        categories.forEach((el) => {
                            const forms = forms_config[el]
                            if (forms) {
                                const titles = Object?.keys(forms)
                                if (titles?.length > 0) {
                                    titles.forEach((el) => {
                                        const title = el
                                        const form = forms?.[el]
                                        if (
                                            form?.['view_structure'] === 'form'
                                        ) {
                                            dispatch(
                                                deleteFormData({
                                                    digital_name: title,
                                                })
                                            )
                                            dispatch(
                                                deleteFormValidity({
                                                    digital_name: title,
                                                })
                                            )
                                            dispatch(
                                                deleteFormAuxiliary({
                                                    digital_name: title,
                                                })
                                            )
                                            if (form?.['auxiliary']) {
                                                dispatch(
                                                    updateFormAuxiliary({
                                                        digital_name: title,
                                                        auxiliary:
                                                            form?.auxiliary,
                                                    })
                                                )
                                            }
                                        } else if (
                                            form?.['view_structure'] ===
                                            'sub-forms'
                                        ) {
                                            const subForms = form?.['sub_forms']
                                            if (subForms?.length > 0) {
                                                subForms.forEach((el) => {
                                                    const subFormTitle = el
                                                    dispatch(
                                                        deleteSubFormData({
                                                            digital_name: title,
                                                            digital_sub_name:
                                                                subFormTitle,
                                                        })
                                                    )
                                                    dispatch(
                                                        deleteSubFormValidity({
                                                            digital_name: title,
                                                            digital_sub_name:
                                                                subFormTitle,
                                                        })
                                                    )
                                                    dispatch(
                                                        deleteSubFormAuxiliary({
                                                            digital_name: title,
                                                            digital_sub_name:
                                                                subFormTitle,
                                                        })
                                                    )
                                                    if (form?.['auxiliary']) {
                                                        dispatch(
                                                            updateSubFormAuxiliary(
                                                                {
                                                                    digital_name:
                                                                        title,
                                                                    digital_sub_name:
                                                                        subFormTitle,
                                                                    auxiliary:
                                                                        form?.auxiliary,
                                                                }
                                                            )
                                                        )
                                                    }
                                                })
                                            }
                                        }
                                    })
                                }
                            }
                        })
                    }
                }
            }
        }

        //// End Clear Existing Data

        // Restore Deep Link
        dispatch(createAuthorizations({ form: form }))

        //// Perform Browser Effects

        console.clear()
        window.location.reload()

        //// End Perform Browser Effects

        // Dispatch Configuration Data To Page & Form Data
        if (local_configuration) {
            // Restore Pages Defaults
            const pages = local_configuration?.['pages']
            if (pages) {
                const pageTitles = Object?.keys(pages)
                if (pageTitles?.length > 0) {
                    pageTitles.forEach((el) => {
                        const title = el
                        const page = page?.[el]
                        if (page?.['auxiliary']) {
                            const auxiliary = page?.auxiliary
                            dispatch(
                                createAccountAuxiliary({
                                    digital_name: title,
                                    auxiliary: auxiliary,
                                })
                            )
                        }
                    })
                }
            }

            // Restore Forms Defaults
            const forms = local_configuration?.['forms']
            if (forms) {
                const formTitles = Object?.keys(forms)
                if (formTitles?.length > 0) {
                    formTitles.forEach((el) => {
                        const title = el
                        const form = forms?.[el]
                        if (form?.['auxiliary']) {
                            const auxiliary = form?.auxiliary
                            dispatch(
                                createFormAuxiliary({
                                    digital_name: title,
                                    auxiliary: auxiliary,
                                })
                            )
                        }
                        if (!form?.['values'] && !form?.['auxiliary']) {
                            const subFormTitles = Object?.keys(form)
                            if (subFormTitles?.length > 0) {
                                subFormTitles.forEach((el) => {
                                    const subTitle = el
                                    const subForm = form?.[el]
                                    if (subForm?.['auxiliary']) {
                                        const subFormAuxiliary =
                                            subForm?.auxiliary
                                        dispatch(
                                            createSubFormValidity({
                                                digital_name: title,
                                                digital_sub_name: subTitle,
                                                auxiliary: subFormAuxiliary,
                                            })
                                        )
                                    }
                                })
                            }
                        }
                    })
                }
            }
        }
    }

    // Configure Configuration, Application, Interface & Page
    const handleConfiguration = async () => {
        const configuration = await createConfiguration()
        const application = await createApplicationLevelConfig()
        const ui = await createInterfaceLevelConfig()
        const page = await createPageLevelConfig(pathname)
        const then = settingsData?.interface?.configured_timestamp
        const now = format(new Date(), "yyyy-MM-dd'T'HH:mm:ssXX").toString()
        const diff = differenceInMinutes(parseISO(now), parseISO(then))
        const frequency = parseInt(
            process.env.REACT_APP_CONFIGURATION_PERSISTANCE_FREQUENCY_MINUTES
        )

        if (configuration && application && ui && page) {
            dispatch(
                updateSettings({
                    digital_name: 'interface',
                    settings: {
                        initialized: true,
                    },
                })
            )

            if (diff > frequency) {
                dispatch(
                    updateSettings({
                        digital_name: 'interface',
                        settings: {
                            configured_timestamp: now,
                        },
                    })
                )
            }

            return await settingsData?.configuration
        } else {
            // Dispatch To Clear Data
            dispatch(
                updateSettings({
                    digital_name: 'configuration',
                    settings: {},
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'application',
                    settings: {},
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'interface',
                    settings: {},
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'category',
                    settings: {},
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'page',
                    settings: {},
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'form',
                    settings: {},
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'interface',
                    settings: {
                        initialized: false,
                        view_type: '',
                        view_title: '',
                        view_links: [],
                    },
                })
            )

            return null
        }
    }

    // Configure Page Level Configuration
    const handlePageConfiguration = async (pathname) => {
        const page = await createPageLevelConfig(pathname)
        if (page) {
            dispatch(
                updateSettings({
                    digital_name: 'interface',
                    settings: {
                        initialized: true,
                    },
                })
            )

            return await settingsData?.page
        } else {
            // Dispatch To Clear Data
            dispatch(
                deleteSettings({
                    digital_name: 'interface',
                })
            )
            dispatch(
                deleteSettings({
                    digital_name: 'category',
                })
            )
            dispatch(
                deleteSettings({
                    digital_name: 'page',
                })
            )
            dispatch(
                deleteSettings({
                    digital_name: 'form',
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'interface',
                    settings: {
                        initialized: false,
                        full_screen_width: fullScreen ? true : false,
                        not_full_screen_width: notFullScreen ? true : false,
                        view_expanded: fullScreen ? true : false,
                        side_nav_expanded: fullScreen ? true : false,
                        view_title: '',
                        view_type: '',
                        view_links: [],
                    },
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'category',
                    settings: {},
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'page',
                    settings: {},
                })
            )
            dispatch(
                updateSettings({
                    digital_name: 'form',
                    settings: {},
                })
            )

            return null
        }
    }

    // Create Session
    const handleSession = async () => {
        // Extra Values From Current Redux State
        const cookie = await authorizationsData?.['tokens']?.['cookie']
        const reguserid = await accountData?.['login']?.['values']?.[
            'reguserid'
        ]

        // Check Redux State Values
        if (cookie && reguserid) {
            // Call Create Session Method
            const session = await createSession()

            // Check For Session Object & Created Session Boolean
            if (session) {
                const now = format(
                    new Date(),
                    "yyyy-MM-dd'T'HH:mm:ssXX"
                ).toString()

                // Dispatch Session Data To Session
                dispatch(
                    createCache({
                        settings: session?.settings,
                        authorizations: session?.authorizations,
                        account: session?.account,
                        forms: session?.forms,
                    })
                )

                // Dispatch Session Data To Settings
                dispatch(
                    updateSettings({
                        digital_name: 'session',
                        settings: {
                            active: true,
                            created: now,
                            saved: now,
                        },
                    })
                )

                return true
            } else {
                return null
            }
        } else {
            return null
        }
    }

    // Save Session
    const handleSessionLog = async () => {
        // Extra Values From Current Redux State
        const cookie = await authorizationsData?.tokens?.cookie
        const reguserid = await accountData?.login?.values?.reguserid

        // Check Redux State Values
        if (cookie && reguserid) {
            // Call Create Session Log Method
            const log = await createSessionLog(cookie, reguserid)

            // Check For Session Object & Created Session Boolean
            if (log) {
                const now = format(
                    new Date(),
                    "yyyy-MM-dd'T'HH:mm:ssXX"
                ).toString()

                // Dispatch Session Data To Settings
                dispatch(
                    updateSettings({
                        digital_name: 'session',
                        settings: {
                            saved: now,
                        },
                    })
                )

                return true
            } else {
                return null
            }
        } else {
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Create & Check Configuration On Load (Setting)
    useEffect(() => {
        handleConfiguration()
        return () => {}
    }, [configuration])

    // Create Page Configuration On Path (Setting)
    useEffect(() => {
        if (pathname) {
            handlePageConfiguration(pathname)
        }
        return () => {}
    }, [pathname])

    // Create Full Screen Width (Setting)
    useEffect(() => {
        if (fullScreen) {
            dispatch(
                updateSettings({
                    digital_name: 'interface',
                    settings: {
                        full_screen_width: true,
                        not_full_screen_width: false,
                        view_expanded: true,
                        side_nav_expanded: true,
                    },
                })
            )
        }
        return () => {}
    }, [fullScreen])

    // Create Not Full Screen Width (Setting)
    useEffect(() => {
        if (notFullScreen) {
            dispatch(
                updateSettings({
                    digital_name: 'interface',
                    settings: {
                        full_screen_width: false,
                        not_full_screen_width: true,
                        view_expanded: false,
                        side_nav_expanded: false,
                    },
                })
            )
        }
        return () => {}
    }, [notFullScreen])

    // Create Category Search Parameter (Setting)
    useEffect(() => {
        if (categorySearchParam) {
            dispatch(createAuthorizations({ category: categorySearchParam }))
        }
        return () => {}
    }, [categorySearchParam])

    // Create Page Search Parameter (Setting)
    useEffect(() => {
        if (pageSearchParam) {
            dispatch(createAuthorizations({ page: pageSearchParam }))
        }
        return () => {}
    }, [pageSearchParam])

    // Create Form Search Parameter (Setting)
    useEffect(() => {
        if (formSearchParam) {
            dispatch(createAuthorizations({ form: formSearchParam }))
        }
        return () => {}
    }, [formSearchParam])

    // Create Client Search Parameter (Setting)
    useEffect(() => {
        if (clientSearchParam) {
            dispatch(createAuthorizations({ client: clientSearchParam }))
        }
        return () => {}
    }, [clientSearchParam])

    // Create Path (Setting)
    useEffect(() => {
        if (location) {
            const { pathname, search } = location
            if (pathname && search) {
                navigate(pathname)
            }
        }
        return () => {}
    }, [location])

    // Create Session (Setting)
    useEffect(() => {
        const createSessionInterval = setInterval(() => {
            handleSession()
        }, 5000)
        return () => clearInterval(createSessionInterval)
    }, [])

    // Save Session (Setting)
    useEffect(() => {
        const saveSessionInterval = setInterval(() => {
            handleSessionLog()
        }, 15000)
        return () => clearInterval(saveSessionInterval)
    }, [])

    //////////////// Debug

    //////////////// Render & Return
    if (!initialized) {
        return (
            <>
                <ScrollRestoration />
                <main>
                    <PageNavigationComponent />
                    <LoadingComponent />
                    <FooterComponent />
                </main>
                <ActivityTimerComponent handleLogout={handleLogout} />
            </>
        )
    } else if (pathname === '/') {
        return (
            <>
                <ScrollRestoration />
                <main>
                    <PageNavigationComponent />
                    <div className="page-loading">{children}</div>
                    <FooterComponent />
                </main>
                <ActivityTimerComponent handleLogout={handleLogout} />
            </>
        )
    } else if (settingsData?.['interface']?.['view_type'] === 'page') {
        return (
            <>
                <ScrollRestoration />
                <main>
                    <PageNavigationComponent />
                    <div className="page-header">
                        <div className="page-logo" />
                        <div className="page-info">
                            <h1 className="page-title">
                                {settingsData?.['interface']?.['view_title']
                                    ? settingsData?.['interface']?.[
                                          'view_title'
                                      ]
                                    : ''}
                            </h1>
                            <BreadcrumbComponent />
                        </div>
                    </div>
                    <div className="page">{children}</div>
                    <FooterComponent />
                </main>
                <ActivityTimerComponent handleLogout={handleLogout} />
            </>
        )
    } else if (settingsData?.['interface']?.['view_type'] === 'form') {
        return (
            <>
                <ScrollRestoration />
                <main>
                    <FormMenuNavigationComponent />
                    <div
                        className={
                            settingsData?.['interface']?.['view_expanded']
                                ? 'page-expanded page-header'
                                : 'page-not-expanded page-header'
                        }
                    >
                        <div className="page-logo" />
                        <div className="page-info">
                            <h1 className="page-title">
                                {settingsData?.['interface']?.['view_title']
                                    ? settingsData?.['interface']?.[
                                          'view_title'
                                      ]
                                    : ''}
                            </h1>
                            <BreadcrumbComponent />
                        </div>
                    </div>
                    <div
                        className={
                            settingsData?.['interface']?.['view_expanded']
                                ? 'page-expanded page'
                                : 'page-not-expanded page'
                        }
                    >
                        {children}
                    </div>
                    <FooterComponent />
                </main>
                <ActivityTimerComponent handleLogout={handleLogout} />
            </>
        )
    } else {
        return (
            <>
                <ScrollRestoration />
                <main>
                    <PageNavigationComponent />
                    <LoadingComponent />
                    <FooterComponent />
                </main>
                <ActivityTimerComponent handleLogout={handleLogout} />
            </>
        )
    }
}

export default Page
